import React, {RefObject} from 'react';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {withPrecision} from 'utilities/numbers';
import {
  DisplayText,
  Icon,
  Stack,
  Tag,
  TextStyle,
  Pagination,
} from '@shopify/polaris';
import {StarFilledMinor} from '@shopify/polaris-icons';
import {
  Heading,
  Paragraph,
  ReviewListing,
  SkeletonReviewListing,
} from 'sections/services-marketplace/experts/components';
import {StarRatingAggregate} from 'components/polaris/react/star-rating-aggregate';
import {Metrics, Review, ReviewFilters} from 'sections/types';

import * as styles from './Reviews.scss';
import {Connection} from 'utilities/graphql/types';

import i18nArgs from '../../locales';

/* tslint:disable */
const JsStarRatingAggregate: any = StarRatingAggregate;
/* tslint:enable */

interface OwnProps {
  onReviewRatingClick(filters?: ReviewFilters): void;
  reviews?: Connection<Review>;
  metrics?: Metrics;
  filteredRating?: number;
  currentServiceHandle: string;
  loading?: boolean;
}

interface State {
  currentPage: number;
}

type CombinedProps = WithI18nProps & OwnProps;

class ProfileReviews extends React.Component<CombinedProps, State> {
  state: State = {
    currentPage: 1,
  };

  scrollRef: RefObject<HTMLDivElement> = React.createRef();

  removeFilter = () => {
    this.props.onReviewRatingClick();
    this.setState({
      currentPage: 1,
    });
  };

  addFilter = (rating: number) => {
    this.props.onReviewRatingClick({rating});
    this.setState({
      currentPage: 1,
    });
  };

  onNextPage = () => {
    this.scrollToRef();
    this.props.onReviewRatingClick({
      rating: this.props.filteredRating,
      after: this.state.currentPage.toString(),
    });
    this.setState(({currentPage}) => ({
      currentPage: currentPage + 1,
    }));
  };

  onPrevPage = () => {
    this.scrollToRef();
    this.props.onReviewRatingClick({
      rating: this.props.filteredRating,
      before: this.state.currentPage.toString(),
    });
    this.setState(({currentPage}) => ({
      currentPage: currentPage > 1 ? currentPage - 1 : 1,
    }));
  };

  scrollToRef = () => {
    if (!this.scrollRef.current) return;
    this.scrollRef.current.scrollIntoView();
  };

  renderHeader() {
    const {metrics, i18n} = this.props;

    return (
      <>
        <div className={styles.Header}>
          <Stack alignment="center" spacing="tight">
            <Stack.Item>
              <DisplayText element="h2" size="medium">
                {i18n.translate('Profile.ProfileReviews.reviewsTitle')}
              </DisplayText>
            </Stack.Item>
            {metrics && metrics.averageRating ? (
              <div className={styles.OverallRating}>
                <Stack alignment="center" spacing="extraTight">
                  <Icon source={StarFilledMinor} color="yellow" />
                  <Heading element="h3">
                    {withPrecision(metrics.averageRating, 1)}
                  </Heading>
                  <TextStyle variation="subdued">
                    ({metrics.totalReviews})
                  </TextStyle>
                </Stack>
              </div>
            ) : null}
          </Stack>
        </div>
        <Paragraph>
          <TextStyle variation="subdued">
            {i18n.translate('Profile.ProfileReviews.reviewsDescription', {
              qualityOfWork: (
                <TextStyle variation="strong">
                  {i18n.translate('Profile.ProfileReviews.qualityOfWork')}
                </TextStyle>
              ),
              communication: (
                <TextStyle variation="strong">
                  {i18n.translate('Profile.ProfileReviews.communication')}
                </TextStyle>
              ),
            })}
          </TextStyle>
        </Paragraph>
      </>
    );
  }

  renderMetrics() {
    const {metrics, filteredRating, i18n} = this.props;

    if (!metrics || !metrics.ratingAggregates) {
      return null;
    }

    return (
      <div className={styles.RatingAggregate}>
        <Stack vertical spacing="extraLoose">
          <JsStarRatingAggregate
            onSelectRating={this.addFilter}
            aggregates={metrics.ratingAggregates}
          />
          {filteredRating && (
            <Stack>
              <Tag key={filteredRating} onRemove={this.removeFilter}>
                {`${filteredRating.toString()} ${i18n.translate(
                  'Profile.ProfileReviews.star',
                  {count: filteredRating}
                )}`}
              </Tag>
            </Stack>
          )}
        </Stack>
      </div>
    );
  }

  renderReviews() {
    const {reviews, currentServiceHandle, loading, i18n} = this.props;

    if (!reviews || !reviews.edges || reviews.edges.length === 0) {
      return (
        <div className={styles.ReviewListings}>
          <div className={styles.NoReviews}>
            <Paragraph>
              <TextStyle variation="subdued">
                {i18n.translate('Profile.ProfileReviews.noReviewsYet')}
              </TextStyle>
            </Paragraph>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.ReviewListings}>
        {loading
          ? [...Array(6).keys()].map(key => <SkeletonReviewListing key={key} />)
          : reviews.edges.map(edge => (
              <ReviewListing
                currentServiceHandle={currentServiceHandle}
                key={edge.node.id}
                review={edge.node}
              />
            ))}
        <Pagination
          onPrevious={this.onPrevPage}
          hasPrevious={reviews.pageInfo.hasPreviousPage}
          hasNext={reviews.pageInfo.hasNextPage}
          onNext={this.onNextPage}
        />
      </div>
    );
  }

  render() {
    return (
      <div className={styles.Container}>
        {this.renderHeader()}
        <div ref={this.scrollRef} />
        {this.renderMetrics()}
        {this.renderReviews()}
      </div>
    );
  }
}

export default withI18n(i18nArgs)(ProfileReviews);
